import React, { useCallback, useEffect, useRef, useState } from 'react';

// The playback controls of the "Shop by TV" player, laid out after HBO Max's own (help.hbomax.com
// "Playback options and controls", TV device and computer, read 2026-10-24):
//   - play/pause, and skip back / forward by 10 seconds, in the middle;
//   - a progress bar you click anywhere on or drag by its dot, with the time REMAINING bottom right;
//   - volume (click to mute, a slider beside it), audio & subtitles, and full screen on the right;
//   - keys: Space play/pause, Left/Right skip 10 s, Up/Down volume, M mute, Esc leaves full screen.
const SKIP_SECONDS = 10;
const HIDE_AFTER_MS = 3000;

const clock = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = (s % 60).toString().padStart(2, '0');
  return h ? `${h}:${m.toString().padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

const Icon = {
  play: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6.75 5.25v13.5a1.5 1.5 0 0 0 2.28 1.28l11.04-6.75a1.5 1.5 0 0 0 0-2.56L9.03 3.97a1.5 1.5 0 0 0-2.28 1.28" /></svg>
  ),
  pause: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><rect width="4.5" height="15" x="5.25" y="4.5" fill="currentColor" rx="1" /><rect width="4.5" height="15" x="14.25" y="4.5" fill="currentColor" rx="1" /></svg>
  ),
  // A circular arrow with "10" inside, back and forward (the old icons said 15 and skipped 10).
  back: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" d="M4.6 9.4A8 8 0 1 1 4 12.5" />
      <path fill="currentColor" d="M3.2 4.6v5.6h5.6z" />
      <text x="12.4" y="15.6" textAnchor="middle" fontSize="7.4" fontWeight="700" fill="currentColor" fontFamily="Manrope, system-ui, sans-serif">10</text>
    </svg>
  ),
  forward: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" d="M19.4 9.4A8 8 0 1 0 20 12.5" />
      <path fill="currentColor" d="M20.8 4.6v5.6h-5.6z" />
      <text x="11.6" y="15.6" textAnchor="middle" fontSize="7.4" fontWeight="700" fill="currentColor" fontFamily="Manrope, system-ui, sans-serif">10</text>
    </svg>
  ),
  volume: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 9.5v5h4l5 4.5V5L7 9.5z" /><path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" /></svg>
  ),
  muted: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 9.5v5h4l5 4.5V5L7 9.5z" /><path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" d="m16 9.5 5 5m0-5-5 5" /></svg>
  ),
  subtitles: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" /><path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" d="M7 12.5h3M12.5 12.5H17M7 15.5h6M15.5 15.5H17" /></svg>
  ),
  enterFull: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" /></svg>
  ),
  exitFull: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" /></svg>
  ),
};

interface TvControlsProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** The player element: full screen is entered on it, so the side sheet stays inside. */
  playerRef: React.RefObject<HTMLElement | null>;
  isPaused: boolean;
  /** Keys belong to the side sheet while it is open (Esc closes it there, not full screen). */
  keysEnabled: boolean;
}

const TvControls: React.FC<TvControlsProps> = ({ videoRef, playerRef, isPaused, keysEnabled }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [awake, setAwake] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [hover, setHover] = useState<{ ratio: number } | null>(null);
  const hideTimer = useRef<number | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // --- follow the video
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const sync = () => {
      setCurrentTime(video.currentTime);
      setDuration(video.duration || 0);
      setVolume(video.volume);
      setMuted(video.muted || video.volume === 0);
    };
    sync();
    const events = ['timeupdate', 'loadedmetadata', 'seeked', 'volumechange', 'durationchange'];
    events.forEach((name) => video.addEventListener(name, sync));
    return () => events.forEach((name) => video.removeEventListener(name, sync));
  }, [videoRef]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === playerRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [playerRef]);

  // --- show on movement, hide 3 s after the last one while playing (always shown while paused)
  const wake = useCallback(() => {
    setAwake(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setAwake(false), HIDE_AFTER_MS);
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return undefined;
    player.addEventListener('mousemove', wake);
    player.addEventListener('touchstart', wake, { passive: true });
    return () => {
      player.removeEventListener('mousemove', wake);
      player.removeEventListener('touchstart', wake);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [playerRef, wake]);

  // --- actions
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  }, [videoRef]);

  const skip = useCallback(
    (seconds: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.min(video.duration || 0, Math.max(0, video.currentTime + seconds));
      wake();
    },
    [videoRef, wake],
  );

  const setVideoVolume = useCallback(
    (next: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.volume = Math.min(1, Math.max(0, next));
      video.muted = video.volume === 0;
    },
    [videoRef],
  );

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted || video.volume === 0) {
      video.muted = false;
      if (video.volume === 0) video.volume = 0.5;
    } else {
      video.muted = true;
    }
  }, [videoRef]);

  const toggleFullscreen = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else player.requestFullscreen?.();
  }, [playerRef]);

  // --- keys (HBO Max's computer shortcuts)
  useEffect(() => {
    if (!keysEnabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (event.metaKey || event.ctrlKey || event.altKey || target?.closest('input:not([type="range"]), textarea, select, [contenteditable="true"]')) return;
      const video = videoRef.current;
      if (!video) return;
      switch (event.key) {
        case ' ':
        case 'k':
          // A focused button would also take Space as a click: the key is ours.
          event.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          skip(-SKIP_SECONDS);
          break;
        case 'ArrowRight':
          event.preventDefault();
          skip(SKIP_SECONDS);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setVideoVolume(video.volume + 0.1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          setVideoVolume(video.volume - 0.1);
          break;
        case 'm':
        case 'M':
          toggleMute();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        default:
          return;
      }
      wake();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keysEnabled, videoRef, togglePlay, skip, setVideoVolume, toggleMute, toggleFullscreen, wake]);

  // --- the progress bar: click anywhere, or drag the dot
  const ratioAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  const seekTo = (ratio: number) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    video.currentTime = ratio * video.duration;
    setCurrentTime(video.currentTime);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setScrubbing(true);
    seekTo(ratioAt(event.clientX));
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const ratio = ratioAt(event.clientX);
    setHover({ ratio });
    if (scrubbing) seekTo(ratio);
  };
  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setScrubbing(false);
  };

  const played = duration ? currentTime / duration : 0;
  const visible = isPaused || awake || scrubbing;

  return (
    <div className={`tv-controls ${visible ? 'is-visible' : ''}`} data-silvr-ui="true" onClick={(event) => event.stopPropagation()}>
      <div className="tv-progress">
        <div
          ref={barRef}
          className={`tv-progress__bar ${scrubbing ? 'is-scrubbing' : ''}`}
          role="slider"
          tabIndex={0}
          aria-label="Progress"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(currentTime)}
          aria-valuetext={`${clock(currentTime)} of ${clock(duration)}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={() => setHover(null)}
        >
          <div className="tv-progress__track">
            {hover && !scrubbing && <div className="tv-progress__hover" style={{ width: `${hover.ratio * 100}%` }} />}
            <div className="tv-progress__fill" style={{ width: `${played * 100}%` }} />
          </div>
          <div className="tv-progress__dot" style={{ left: `${played * 100}%` }} />
          {hover && (
            <div className="tv-progress__tooltip" style={{ left: `${hover.ratio * 100}%` }}>
              {clock(hover.ratio * duration)}
            </div>
          )}
        </div>
        <span className="tv-progress__remaining" aria-label={`${clock(duration - currentTime)} remaining`}>
          -{clock(duration - currentTime)}
        </span>
      </div>

      <div className="tv-controls__row">
        <div className="tv-controls__side" />
        <div className="tv-controls__center">
          <button type="button" className="tv-ctrl" onClick={() => skip(-SKIP_SECONDS)} aria-label="Back 10 seconds" title="Back 10 seconds (←)">
            {Icon.back}
          </button>
          <button type="button" className="tv-ctrl" onClick={togglePlay} aria-label={isPaused ? 'Play' : 'Pause'} title={`${isPaused ? 'Play' : 'Pause'} (Space)`}>
            {isPaused ? Icon.play : Icon.pause}
          </button>
          <button type="button" className="tv-ctrl" onClick={() => skip(SKIP_SECONDS)} aria-label="Forward 10 seconds" title="Forward 10 seconds (→)">
            {Icon.forward}
          </button>
        </div>
        <div className="tv-controls__side tv-controls__side--right">
          <div className="tv-volume">
            <button type="button" className="tv-ctrl" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} title={`${muted ? 'Unmute' : 'Mute'} (M)`}>
              {muted ? Icon.muted : Icon.volume}
            </button>
            <input
              type="range"
              className="tv-volume__slider"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(event) => setVideoVolume(parseFloat(event.target.value))}
              aria-label="Volume"
              style={{ '--level': `${(muted ? 0 : volume) * 100}%` } as React.CSSProperties}
            />
          </div>
          <button type="button" className="tv-ctrl" disabled aria-label="Audio and subtitles (none for this clip)" title="Audio and subtitles: none for this clip">
            {Icon.subtitles}
          </button>
          <button type="button" className="tv-ctrl" onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} title={`${isFullscreen ? 'Exit full screen (Esc)' : 'Full screen (F)'}`}>
            {isFullscreen ? Icon.exitFull : Icon.enterFull}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TvControls;
